import {
  type CreateOrderInput,
  type CreatedOrder,
  type OrderCreator,
  type OrderPaymentConfirmer,
  type OrderReader,
  type UserOrder,
  OrderType,
} from '../../domain/order/order';

interface RegisterOrderResponse {
  ID?: string;
  id?: string;
}

interface PaymentConfirmationResponse {
  success?: boolean;
}

interface ApiOrder {
  ID?: string;
  id?: string;
  Type?: number;
  type?: number;
  Status?: number;
  status?: number;
  Size?: string;
  size?: string;
  JerseyName?: string;
  jersey_name?: string;
  JerseyNumber?: string;
  jersey_number?: string;
  DonationAmount?: string;
  donation_amount?: string;
  PaymentConfirmationID?: string;
  payment_confirmation_id?: string;
  CreatedAt?: string;
  created_at?: string;
}

export interface OrderApiOptions {
  baseUrl: string;
  paymentUrls: {
    register: string;
    jersey: string;
    tshirt: string;
    donation: {
      10: string;
      25: string;
      50: string;
      100: string;
    };
  };
}

export class OrderApi
  implements OrderCreator, OrderReader, OrderPaymentConfirmer
{
  constructor(private readonly options: OrderApiOptions) {}

  async createOrder(input: CreateOrderInput): Promise<CreatedOrder> {
    const shirtType = input.shirtType ?? 'jersey';
    const donationAmount = Number(input.amount ?? 0);
    const response = await fetch(
      `${this.options.baseUrl}/api/v1/orders/register-order`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: input.email,
          ...(input.phone ? { phone: input.phone } : {}),
          donation_type:
            input.kind === 'register'
              ? OrderType.Inscription
              : input.kind === 'donate'
                ? OrderType.Donation
                : shirtType === 'jersey'
                  ? OrderType.Jersey
                  : OrderType.TShirt,
          jersey_style: shirtType === 'jersey' ? 1 : 2,
          size: input.kind === 'donate' ? 'N/A' : input.size,
          jersey_name: input.jerseyName ?? 'N/A',
          jersey_number: input.jerseyNumber ?? 'N/A',
          amount:
            input.amount ??
            (input.kind === 'register' ? 40 : shirtType === 'jersey' ? 40 : 30),
        }),
      }
    );

    if (!response.ok) {
      throw new Error('No pudimos crear la orden. Intenta nuevamente.');
    }

    const order = (await response.json()) as RegisterOrderResponse;
    const id = order.ID ?? order.id;
    if (!id) {
      throw new Error('La orden fue creada sin un identificador válido.');
    }

    return {
      id,
      paymentUrl:
        input.kind === 'register'
          ? this.options.paymentUrls.register
          : input.kind === 'donate'
            ? donationAmount === 10
              ? this.options.paymentUrls.donation[10]
              : donationAmount === 25
                ? this.options.paymentUrls.donation[25]
                : donationAmount === 50
                  ? this.options.paymentUrls.donation[50]
                  : this.options.paymentUrls.donation[100]
            : shirtType === 'jersey'
              ? this.options.paymentUrls.jersey
              : this.options.paymentUrls.tshirt,
    };
  }

  async getOrdersByUserEmail(email: string): Promise<UserOrder[]> {
    const response = await fetch(
      `${this.options.baseUrl}/api/v1/orders?email=${encodeURIComponent(email)}`
    );

    if (!response.ok) {
      throw new Error('No pudimos consultar tus órdenes. Intenta nuevamente.');
    }

    const orders = (await response.json()) as ApiOrder[];
    return orders.map((order) => ({
      id: order.ID ?? order.id ?? '',
      type: order.Type ?? order.type ?? 0,
      status: order.Status ?? order.status ?? 0,
      size: order.Size ?? order.size ?? '',
      jerseyName: order.JerseyName ?? order.jersey_name ?? '',
      jerseyNumber: order.JerseyNumber ?? order.jersey_number ?? '',
      donationAmount: order.DonationAmount ?? order.donation_amount ?? '',
      paymentConfirmationId:
        order.PaymentConfirmationID ?? order.payment_confirmation_id ?? '',
      createdAt: order.CreatedAt ?? order.created_at ?? '',
    }));
  }

  async updateOrderConfirmationId(
    orderId: string,
    paymentConfirmationId: string
  ): Promise<boolean> {
    const response = await fetch(
      `${this.options.baseUrl}/api/v1/orders/update-confirmation-id`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          payment_confirmation_id: paymentConfirmationId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        'No pudimos vincular la confirmación con tu orden. Intenta nuevamente.'
      );
    }

    const result = (await response.json()) as PaymentConfirmationResponse;
    return result.success === true;
  }
}

